import axios, { AxiosError } from 'axios';
import logger from '../logger';

const MC_API_BASE_URL = 'https://ymtxwb55dw.us-east-2.awsapprunner.com';
const MC_API_TIMEOUT = 10000;

/**
 * Log entry interface matching the API schema
 */
export interface McApiLogEntry {
  userId: string;
  timestamp: string;
  durationMs: number;
  appVersion: string;
  robot: string;
  data: string | null;
  status: string;
  logSessionId: string | null;
  eventType: string;
  id?: number;
  lastConnectedRobot: string;
  jobId?: string;
}

/**
 * Response from the log upload API
 */
export interface LogUploadResponse {
  success: boolean;
  uploaded: number;
  errors?: string[];
}

/**
 * Mission Control API Service
 * Provides methods to interact with the Mission Control FastAPI backend
 */
export class McApi {
  private baseUrl: string;
  private timeout: number;

  constructor(baseUrl: string = MC_API_BASE_URL, timeout: number = MC_API_TIMEOUT) {
    this.baseUrl = baseUrl;
    this.timeout = timeout;
  }

  /**
   * Upload logs to DynamoDB via the API
   * @param logs Array of log entries to upload
   * @returns Response with upload status
   */
  async sendLogsToDynamo(logs: McApiLogEntry[]): Promise<LogUploadResponse> {
    try {
      const response = await axios.post<{ message: string; uploaded: number }>(`${this.baseUrl}/log`, logs, {
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        timeout: this.timeout,
      });

      return {
        success: true,
        uploaded: response.data.uploaded || logs.length,
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Failed to upload logs to DynamoDB:', axiosError);

      await logger.log('MC_API_ADD_LOGS_ERROR', {
        error: axiosError.message,
        status: axiosError.response?.status,
        logsCount: logs.length,
      });

      return {
        success: false,
        uploaded: 0,
        errors: [axiosError.message],
      };
    }
  }

  /**
   * Upload a file to S3
   * @param file File or Blob to upload
   * @param bucket S3 bucket name
   * @param key S3 key/path for the file
   * @returns Upload response with S3 URL or error
   */
  async uploadFileToS3(
    file: File | Blob,
    bucket: string,
    key: string,
  ): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post<{ url: string; message: string }>(`${this.baseUrl}/upload`, formData, {
        params: {
          bucket,
          key,
        },
        headers: {
          'Content-Type': 'multipart/form-data',
          accept: 'application/json',
        },
        timeout: this.timeout * 3, // Longer timeout for file uploads
      });

      return {
        success: true,
        url: response.data.url,
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Failed to upload to S3:', axiosError);

      await logger.log('MC_API_UPLOAD_S3_ERROR', {
        error: axiosError.message,
        status: axiosError.response?.status,
        bucket,
        key,
      });

      return {
        success: false,
        error: axiosError.message,
      };
    }
  }

  /**
   * Download a file from S3
   * @param bucket S3 bucket name
   * @param key S3 key/path of the file to download
   * @returns File blob or error
   */
  async downloadFileFromS3(bucket: string, key: string): Promise<{ success: boolean; blob?: Blob; error?: string }> {
    try {
      const response = await axios.get<ArrayBuffer>(`${this.baseUrl}/download`, {
        params: { bucket, key },
        responseType: 'arraybuffer',
        timeout: this.timeout * 3, // Longer timeout for file downloads
        headers: {
          accept: 'application/json',
        },
      });

      return {
        success: true,
        blob: new Blob([response.data]),
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Failed to download from S3:', axiosError);

      await logger.log('MC_API_DOWNLOAD_S3_ERROR', {
        error: axiosError.message,
        status: axiosError.response?.status,
        bucket,
        key,
      });

      return {
        success: false,
        error: axiosError.message,
      };
    }
  }

  /**
   * Fetch recent NAIP historical years for a location from Earth Engine API
   * @param lon - Longitude of the location
   * @param lat - Latitude of the location
   * @param bounds - Optional bounding box for the area of interest
   * @param count - Number of recent years to fetch (default: 3)
   * @param cacheId - Optional cache/job ID for organizing cached map IDs
   * @param forceRefresh - Whether to force refresh and ignore cache (default: false)
   * @returns Response with array of available years with map IDs
   */
  async fetchRecentHistoricalYears(
    lon: number,
    lat: number,
    bounds?: { xmin: number; ymin: number; xmax: number; ymax: number },
    count: number = 3,
    cacheId?: string,
    forceRefresh: boolean = false,
  ): Promise<{
    success: boolean;
    years?: Array<{
      year: number;
      mapId: string;
      token: string;
      tileUrl: string;
      generatedAt: string;
    }>;
    error?: string;
  }> {
    try {
      // Build query parameters
      const params = new URLSearchParams({
        lon: lon.toString(),
        lat: lat.toString(),
        count: count.toString(),
      });

      if (bounds) {
        params.append('bounds', `${bounds.xmin},${bounds.ymin},${bounds.xmax},${bounds.ymax}`);
      }

      if (cacheId) {
        params.append('cache_id', cacheId);
      }

      if (forceRefresh) {
        params.append('force_refresh', 'true');
      }

      const url = `${this.baseUrl}/earth-engine/naip/recent-years?${params.toString()}`;

      const response = await axios.get<{
        years: Array<{
          year: number;
          mapId: string;
          token: string;
          tileUrl: string;
          generatedAt: string;
        }>;
      }>(url, {
        headers: {
          accept: 'application/json',
        },
        timeout: this.timeout * 3, // Longer timeout for Earth Engine API
      });

      return {
        success: true,
        years: response.data.years,
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('Failed to fetch recent historical years:', axiosError);

      await logger.log('MC_API_FETCH_HISTORICAL_YEARS_ERROR', {
        error: axiosError.message,
        status: axiosError.response?.status,
        lon,
        lat,
      });

      return {
        success: false,
        error: axiosError.message,
      };
    }
  }

  /**
   * Check if the API is accessible
   * @returns true if API is reachable
   */
  async isAlive(): Promise<boolean> {
    try {
      const response = await axios.get(`${this.baseUrl}/health`, {
        timeout: 2000,
      });

      return response.status >= 200 && response.status < 300;
    } catch (error) {
      return false;
    }
  }
}

// Export a singleton instance
export const mcApi = new McApi();
